// ============================================================
// SideScroll.tsx — a side column on a three-column screen (case menu, client
// menu, pinned notes, the settlement preview, a bill's totals). The middle of
// the page scrolls in <main>; a side column stays put and scrolls by itself
// when the mouse is over it, without chaining into the page.
//   • No visible scrollbar: a soft fade at the bottom edge says there's more,
//     and goes at the end.
//   • It's as tall as the room left in <main> (the hero scrolling away gives it
//     more), so its last item can always be reached.
//   • Keyboard: focusable while it overflows, so arrows and PageUp/Down work;
//     Tab into a link scrolls it into view as usual.
//   • From `from` px up only: below that the page is one normal column.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'

const GAP = 16

export const sideColumnHeight = (columnTop: number, areaTop: number, areaBottom: number): number =>
  Math.max(120, areaBottom - Math.max(columnTop, areaTop) - GAP)

const SideScroll: React.FC<{ children: React.ReactNode; from?: number; className?: string; label?: string }> = ({ children, from = 768, className = '', label }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState(false)
  const [overflows, setOverflows] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const area = el.closest('main')
    const wide = window.matchMedia?.(`(min-width: ${from}px)`)
    const measure = (): void => {
      if (!wide?.matches || !area) { el.style.maxHeight = ''; setMore(false); setOverflows(false); return }
      const a = area.getBoundingClientRect()
      el.style.maxHeight = `${sideColumnHeight(el.getBoundingClientRect().top, a.top, a.bottom)}px`
      setOverflows(el.scrollHeight > el.clientHeight + 1)
      setMore(el.scrollTop + el.clientHeight < el.scrollHeight - 2)
    }
    let frame = 0
    const later = (): void => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure) }
    measure()
    el.addEventListener('scroll', later, { passive: true })
    area?.addEventListener('scroll', later, { passive: true })
    window.addEventListener('resize', later)
    const seen = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(later)
    seen?.observe(el)
    if (el.firstElementChild) seen?.observe(el.firstElementChild)
    return () => {
      cancelAnimationFrame(frame)
      el.removeEventListener('scroll', later)
      area?.removeEventListener('scroll', later)
      window.removeEventListener('resize', later)
      seen?.disconnect()
    }
  }, [from])
  return (
    <div className="relative min-w-0">
      <div ref={ref} className="casevo-side-scroll" data-side-scroll="" tabIndex={overflows ? 0 : undefined} aria-label={overflows ? label : undefined} role={overflows && label ? 'group' : undefined}>
        <div className={className}>{children}</div>
      </div>
      {more && <span aria-hidden="true" className="casevo-side-fade" data-side-fade="" />}
    </div>
  )
}

export default SideScroll
